import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import ReactPlayer from "react-player";
import {Button} from "primereact/button";
import {Slider} from "primereact/slider";
import "./VideoPlayer.css";
import {Dropdown} from "primereact/dropdown";
import VideoThumbnail from "./VideoThumbnail";
import {useVideoLogger} from "../../hooks/logger/useVideoLogger";
import CaptionMenu from "./CaptionMenu";

// Güvenli 3 hane yuvarlama helper'ı (null/NaN korumalı)
const round3 = (v) => Number.isFinite(v) ? Math.round(v * 1000) / 1000 : 0;

//Played Segments eklenebilir.
//frameRate eklenebilir.
//length eklenebilir.
const VideoPlayer = forwardRef((props, ref) => {
    const {fullWidth, setFullWidth, setCurrentTime, note, addNote, captions = [], onTranscriptToggle, onActiveCaptionChange, isTranscriptOpen = false, onFullscreenChange} = props;
    // const refReactPlayer = React.createRef();

    const customPlayerRef = useRef(null);
    const playerRef = useRef(null);
    const controlsRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [volume, setVolume] = useState(60);
    const [muted, setMuted] = useState(false);
    const [seeking, setSeeking] = useState(false);
    const [played, setPlayed] = useState(0);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [duration, setDuration] = useState(0);
    const [playedSeconds, setPlayedSeconds] = useState(0);
    const [fullscreen, setFullscreen] = useState(false) // Yeni state
    const [isMouseOnVideo, setIsMouseOnVideo] = useState(false);
    const [activeCaptionIndex, setActiveCaptionIndex] = useState(null); // null = kapalı, 0+ = aktif caption index
    const [activeCueText, setActiveCueText] = useState('');

    const [resolvedUrl, setResolvedUrl] = useState(null);
    const hideControlsTimeoutRef = useRef(null);

    const previousVolumeRef = useRef(volume);

    const playerWrapperRef = useRef(null);
    const log = useVideoLogger(props.assetId);

    // Caption toggle handler - log işlemleri VideoPlayer içinde
    const handleCaptionToggle = (index) => {
        if (activeCaptionIndex === index) {
            setActiveCaptionIndex(null); // Kapalı yap
            log("captionOff", { time: getCurrentTime() });
            onActiveCaptionChange?.(null); // Parent'e bildir
        } else {
            setActiveCaptionIndex(index); // Aktif yap
            log("captionOn", {
                time: getCurrentTime(),
                captionIndex: index,
                locale: captions[index]?.localeId
            });
            onActiveCaptionChange?.(index); // Parent'e bildir
        }
    };

    const playbackSpeedOptions = [{label: '0.25x', value: 0.25}, {label: '0.5x', value: 0.5}, {
        label: '0.75x',
        value: 0.75
    }, {label: '1x', value: 1}, {label: '1.25x', value: 1.25}, {label: '1.5x', value: 1.5}, {label: '2x', value: 2},];

    // EKLENDI: Kontrol barının tutarlı gizlenmesi için merkezi görünürlük state'i
    const [controlsVisible, setControlsVisible] = useState(true); // true -> görünür, false -> gizli (hide class)

    useImperativeHandle(ref, () => ({
        getCurrentTime: () => getCurrentTime(),
        getDuration: () => duration,
        getInternalPlayer: () => playerRef.current?.getInternalPlayer?.(),
        handlePlayPause: () => handlePlayPause(),
        isPlaying: () => playing,
        seekTo: (time) => {
            if (playerRef.current) {
                const timeFrom = getCurrentTime();
                playerRef.current.seekTo(time, 'seconds');
                setSeeking(false); // Seeking tamamlandı
                // Progress'i güncelle
                if (duration > 0) {
                    setPlayed(time / duration);
                    setPlayedSeconds(time);
                }
                // Log at (transcript'ten seek için)
                log("seeked", { timeFrom: round3(timeFrom), timeTo: round3(time), source: "transcript" });
            }
        },
        // başka özel fonksiyonlar da ekleyebilirsin
    }));

    // useEffect(() => {
    //     if (playing && !resolvedUrl) {
    //         setResolvedUrl(props.url);
    //     }
    // }, [playing]);


    // useEffect(() => {
    //     //Change the title based on page visibility
    //     if (isVisible) {
    //         setPlaying(true);
    //     } else {
    //         setPlaying(false);
    //     }
    //     console.log(actions);
    // }, [isVisible]);

    useEffect(() => {
        // Fullscreenchange eventini dinleyen bir fonksiyon
        const handleFullscreenChange = () => {
            // Fullscreen state'ini document.fullscreenElement'e göre ayarla
            const isFullscreen = document.fullscreenElement !== null;
            setFullscreen(isFullscreen);
            // Parent'a fullscreen durumunu ve container ref'ini bildir
            onFullscreenChange?.(isFullscreen, customPlayerRef.current);
        }
        document.addEventListener("fullscreenchange", handleFullscreenChange)
        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange)
        }
    }, [onFullscreenChange]);

    useEffect(() => {
        setPlaying(false);
        setPlayed(0);
        setPlayedSeconds(0);

        props.onPlayingChange?.(false);
    }, [props.url]);

    // Control caption track visibility
    useEffect(() => {
        try {
            const videoElement = playerRef.current?.getInternalPlayer?.();
            if (videoElement && videoElement.textTracks) {
                for (let i = 0; i < videoElement.textTracks.length; i++) {
                    videoElement.textTracks[i].mode = 'hidden';
                }
                if (activeCaptionIndex !== null && videoElement.textTracks[activeCaptionIndex]) {
                    const track = videoElement.textTracks[activeCaptionIndex];
                    track.mode = 'hidden';
                    const onCue = () => setActiveCueText(track.activeCues?.[0]?.text || '');
                    track.addEventListener('cuechange', onCue);
                    return () => track.removeEventListener('cuechange', onCue);
                } else {
                    setActiveCueText('');
                }
            }
        } catch (error) {
            console.error('Caption track control error:', error);
        }
    }, [activeCaptionIndex]);

    // Bileşen unmount olurken terminated logla
    useEffect(() => {
        return () => {
            try {
                const progress = (duration > 0) ? round3(playedSeconds / duration) : 0;
                if (playing) {
                    log("paused", { time: getCurrentTime(), progress });
                }
                log("terminated", { time: getCurrentTime(), progress });
            } catch (_) { /* yut */ }
        };
    }, []);

    const handleEnded = () => {
        setPlaying(false);
        props.onPlayingChange?.(false);
        const progress = (duration > 0) ? round3(playedSeconds / duration) : 0;
        log("completed", { time: getCurrentTime(), progress });
        props.onEnded?.();
    };

    const handlePlayPause = () => {
        const willPlay = !playing;
        setPlaying(willPlay);

        if (willPlay) {
            if (getCurrentTime() === 0) {
                log("initialized", {
                    speed: playbackSpeed,
                    volume: round3(volume / 100),
                    muted: muted,
                    fullscreen: fullscreen,
                    fullWidth: fullWidth,
                    screenSize: window.screen.width + "x" + window.screen.height,
                    videoPlaybackSize: playerWrapperRef.current ?
                        (playerWrapperRef.current.clientWidth + "x" + playerWrapperRef.current.clientHeight)
                        : null,
                    // frameRate: getTotalVideoFrames(),
                    // length: duration
                });
            }
            log("played", { time: getCurrentTime() });

            // Video oynatılmaya başlandığında timeout başlat
            if (hideControlsTimeoutRef.current) {
                clearTimeout(hideControlsTimeoutRef.current);
            }
            hideControlsTimeoutRef.current = setTimeout(() => {
                setControlsVisible(false);
            }, 3000);
        } else {
            const progress = (duration > 0) ? round3(playedSeconds / duration) : 0;
            log("paused", { time: getCurrentTime(), progress });

            // Video durdurulduğunda timeout'u temizle ve kontrolleri görünür tut
            if (hideControlsTimeoutRef.current) {
                clearTimeout(hideControlsTimeoutRef.current);
            }
            setControlsVisible(true);
        }
        props.onPlayingChange?.(willPlay);
    };

    // const getTotalVideoFrames = () => {
    //     try {
    //         const internal = playerRef.current?.getInternalPlayer?.();
    //         if (internal && typeof internal.getVideoPlaybackQuality === 'function') {
    //             const q = internal.getVideoPlaybackQuality();
    //             return q?.totalVideoFrames ?? null;
    //         }
    //         return null;
    //     } catch { return null; }
    // };

    const handleProgress = (state) => {
        if (!seeking) {
            setPlayed(state.played);
        }
        setPlayedSeconds(state.playedSeconds);
        setCurrentTime(state.playedSeconds);
        // log("Progress", { time: playerRef.current.getCurrentTime(), played: state.played, playedSeconds: state.playedSeconds});
    };

    const handleSeekChange = (e) => {
        const newPlayed = parseFloat(e.target.value); // bu 0.0 - 1.0 arasında
        setPlayed(newPlayed);
        // playerRef.current.seekTo(parseFloat(e.value));
        // log("seekChange", { time: getCurrentTime(), to: newPlayed * duration });
    };

    const handleSeekMouseUp = (e) => {
        const newPlayed = parseFloat(e.target.value); // bu 0.0 - 1.0 arasında
        setSeeking(false);
        playerRef.current.seekTo(parseFloat(e.target.value));
        const timeTo = round3(newPlayed * duration);
        log("seeked", { timeTo });
    };

    const handleSeekMouseDown = (e) => {
        setSeeking(true);
        log("seeking", { timeFrom: getCurrentTime() });
    };

    const handleRewind = () => {
        let time = playerRef.current.getCurrentTime();
        playerRef.current.seekTo(playerRef.current.getCurrentTime() - 5, 'seconds');
        if ((playedSeconds - 5) > 0) {
            setPlayedSeconds(playedSeconds - 5);
        }
        log("rewind", { timeFrom: round3(time || 0), timeTo: getCurrentTime() });
    };

    const handleForward = () => {
        let time = playerRef.current.getCurrentTime();
        playerRef.current.seekTo(playerRef.current.getCurrentTime() + 5, 'seconds');
        if ((playedSeconds + 5) < duration) {
            setPlayedSeconds(playedSeconds + 5);
        }
        log("forward", { timeFrom: round3(time || 0), timeTo: getCurrentTime() });
    };

    const handlePlaybackSpeed = (e) => {
        setPlaybackSpeed(e.value);
        log("speed", { time: getCurrentTime(), speed: e.value });
    };

    const handleVolume = (e) => {
        setVolume(e.value);
    };

    const handleVolumeCompleted = (e) =>{
        let newVolume = e.value;
        if (previousVolumeRef.current !== newVolume) {
            log("volume", { time: getCurrentTime(), volumeFrom: round3(previousVolumeRef.current / 100), volumeTo: round3(newVolume / 100) });
            previousVolumeRef.current = newVolume;
        }
    }

    const handleMute = () => {
        setMuted(!muted);
        log((muted ? "mute" : "unmute"), { time: getCurrentTime(), muted: !muted });
    };

    const handleDuration = (duration) => {
        setDuration(duration);
    };

    const formatDuration = (seconds) => {
        return new Date(seconds * 1000).toISOString().substr(11, 8);
    };

    const handleMouseMove = () => {
        // Kontrolleri görünür yap
        setControlsVisible(true);

        // Önceki timeout'u temizle
        if(hideControlsTimeoutRef.current) {
            clearTimeout(hideControlsTimeoutRef.current);
        }

        // Sadece video oynatılıyorsa otomatik gizleme timeout'u başlat
        if (playing) {
            hideControlsTimeoutRef.current = setTimeout(() => {
                setControlsVisible(false);
            }, 3000); // 3 saniye hareketsizlik sonrası gizle
        }

        if(!isMouseOnVideo) {
            log("mouseOnVideo",  { time: getCurrentTime()});
        }
        setIsMouseOnVideo(true);
    };

    const handleMouseLeave = () => {
        // Timeout'u temizle
        if (hideControlsTimeoutRef.current) {
            clearTimeout(hideControlsTimeoutRef.current);
        }

        // Video oynatılıyorsa kontrolleri hemen gizle, aksi halde görünür bırak
        if (playing) {
            setControlsVisible(false);
        }

        log("mouseLeaveVideo",  { time: getCurrentTime()});
        setIsMouseOnVideo(false);
    };

    // Kontroller üzerindeyken timeout'u temizle
    const handleControlsMouseEnter = () => {
        if (hideControlsTimeoutRef.current) {
            clearTimeout(hideControlsTimeoutRef.current);
        }
        setControlsVisible(true);
    };

    // Kontroller üzerinden ayrılınca yeni timeout başlat
    const handleControlsMouseLeave = () => {
        if (playing) {
            if (hideControlsTimeoutRef.current) {
                clearTimeout(hideControlsTimeoutRef.current);
            }
            hideControlsTimeoutRef.current = setTimeout(() => {
                setControlsVisible(false);
            }, 3000);
        }
    };

    const handleFullscreen = () => {
        if (fullscreen) {
            // Full screen modundan çık
            document.exitFullscreen();
            setFullscreen(false);
            log("fullscreen", { time: getCurrentTime(), fullscreen: false});
        } else {
            // Full screen moduna gir
            customPlayerRef.current.requestFullscreen();
            setFullscreen(true);
            log("fullscreen", { time: getCurrentTime(), fullscreen: true });
        }
    }


    const handleFullWidth = () => {
        setFullWidth();
        log("fullWidth", { time: getCurrentTime(), fullWidth: !fullWidth});
    }

    // const handleSeekEndForSlider = (e) => {
    //     // Seeking bittiğinde sesi aç
    //     playerRef.current.seekTo(e.value)
    //     setSeeking(false);
    // }
    //
    // const handleSeekChangeForSlider = (e) => {
    //     setPlayed(e.value)
    //     setSeeking(true);
    // }

    const handleNote = () => {
        addNote();
        log("note", { time: getCurrentTime()});
    }

    const getCurrentTime = () => {
        const raw = playerRef.current?.getCurrentTime ? playerRef.current.getCurrentTime() : 0;
        const num = (typeof raw === 'number' && isFinite(raw)) ? raw : 0;
        return round3(num);
    };


    return <>
        <div className={`custom-player ${playing && !controlsVisible ? 'controls-hidden' : ''}`} ref={customPlayerRef}
             onMouseMove={handleMouseMove}
             onMouseLeave={handleMouseLeave}
        >
            <div>
                <div className="custom-container" ref={playerWrapperRef} onClick={handlePlayPause}>
                    <ReactPlayer {...props}
                                 key={props.url}
                                 // url={resolvedUrl}
                        // Disable download button
                                 config={{
                                     file: {
                                         attributes: {
                                             controlsList: 'noplaybackrate nodownload',
                                             preload: "none",
                                             crossOrigin: "anonymous"
                                         },
                                         tracks: captions.map((caption, index) => ({
                                             kind: 'subtitles',
                                             src: caption.url,
                                             srcLang: caption.locale_id || 'tr',
                                             label: caption.video_label || caption.title || `Caption ${index + 1}`,
                                             default: index === activeCaptionIndex
                                         }))
                                     }
                                 }}
                        // Disable right click
                                 onContextMenu={e => e.preventDefault()}
                                 controls={false}
                                 pip={false}
                                 ref={playerRef}
                                 playing={playing}
                                 volume={parseFloat(volume / 100)}
                                 muted={muted}
                                 playbackRate={playbackSpeed}
                                 onProgress={handleProgress}
                                 onDuration={handleDuration}
                                 onEnded={handleEnded}   // <- ensure onEnded is wired
                                 played={played}
                                 width='100%'
                                 height='100%'
                                 light={
                                     <VideoThumbnail
                                     videoUrl={props.url || props.url}
                                     width={720}
                                     height={405} // 16:9 oranında örnek
                                     // onClick={() => setPlaying(true)}
                                     />
                                 }
                                 // playIcon={
                                 //         <button onClick={() => setPlaying(true)}
                                 //                 className="video-play-btn video-popup-activation">
                                 //             <i className="icon-18"></i>
                                 //         </button>}
                                 // playIcon={
                                 //         <button onClick={() => setPlaying(true)}
                                 //                 className="video-play-btn video-popup-activation">
                                 //             <i className="icon-18"></i>
                                 //         </button>}
                    />
                </div>
                {activeCueText && <div className="custom-subtitle-overlay"><span>{activeCueText}</span></div>}
                {/* YENI: state tabanlı eş zamanlı fade için className güncellendi */}
                <div
                    className={`controls ${playing && !controlsVisible ? 'hide' : ''}`}
                    ref={controlsRef}
                    onMouseEnter={handleControlsMouseEnter}
                    onMouseLeave={handleControlsMouseLeave}
                >
                    <div className="progress-wrapper">
                        <input
                            type='range' min={0} max={0.999999} step='any'
                            value={played}
                            onMouseDown={handleSeekMouseDown}
                            onChange={handleSeekChange}
                            onMouseUp={handleSeekMouseUp}
                        />
                    </div>
                    {/*<Slider*/}
                    {/*    max={1}*/}
                    {/*    step={0.01}*/}
                    {/*    value={played}*/}
                    {/*    onChange={handleSeekChangeForSlider}*/}
                    {/*    onSlideEnd={handleSeekEndForSlider}*/}
                    {/*    style={{width: '100%'}}/>*/}
                    {/*<Slider*/}
                    {/*    value={played}*/}
                    {/*    max={1}*/}
                    {/*    step={0.01}*/}
                    {/*    onChange={handleSeekChange}*/}
                    {/*    // onMouseUp={handleSeekMouseUp}*/}
                    {/*    // onMouseDown={handleSeekMouseDown}*/}
                    {/*    // onTouchEnd={handleSeekMouseUp}*/}
                    {/*    style={{width: '100%'}}/>*/}
                    <div className="subcontrols">
                        <div className="left-controls">
                            <Button icon={playing ? "pi pi-pause" : "pi pi-play"} size="lg" rounded text
                                    onClick={handlePlayPause}/>
                            <Button icon="pi pi-replay" size="lg" rounded text onClick={handleRewind}/>
                            <Dropdown value={playbackSpeed} onChange={handlePlaybackSpeed}
                                      options={playbackSpeedOptions}
                                      optionLabel="label" className="p-inputtext-sm"
                                      // appendTo={customPlayerRef.current}
                                      appendTo={fullscreen ? customPlayerRef.current : document.body}
                            />
                            <Button icon="pi pi-refresh" size="lg" rounded text onClick={handleForward}/>
                            <p>
                                <span>{formatDuration(playedSeconds)}</span> / <span>{formatDuration(duration)}</span>
                            </p>
                            {/* Fullscreen modunda note butonu gizli */}
                            {note && !fullscreen && <Button icon="pi pi-file-edit" size="lg" rounded text onClick={handleNote}/>}
                        </div>
                        <div className="right-controls">
                            {/*<div onMouseEnter={(e) => setShowVolumeSlider(true)} onMouseLeave={(e) => setShowVolumeSlider(false)}>*/}
                            <div className="flex flex-row align-items-center">
                            <Button icon={(muted || volume === 0) ? "pi pi-volume-off" : "pi pi-volume-up"} size="lg"
                                    rounded text onClick={handleMute}/>
                                <div style={{width: '100px', marginLeft: "10px"}}>
                                {!muted && <Slider value={volume} onChange={handleVolume} onSlideEnd={handleVolumeCompleted} max={100}/>}
                                </div>
                            </div>

                            {/* Transcript Button - Fullscreen modunda gizli, CaptionMenu'nün solunda */}
                            {!fullscreen && captions.length > 0 && onTranscriptToggle && (
                                <Button
                                    icon="pi pi-list"
                                    size="lg"
                                    rounded
                                    text
                                    onClick={() => {
                                        onTranscriptToggle();
                                        // Log transcript açma/kapama durumunu
                                        if (isTranscriptOpen) {
                                            log("transcriptOff", { time: getCurrentTime() });
                                        } else {
                                            log("transcriptOn", {
                                                time: getCurrentTime(),
                                                captionIndex: activeCaptionIndex,
                                                locale: activeCaptionIndex !== null ? captions[activeCaptionIndex]?.localeId : null
                                            });
                                        }
                                    }}
                                    style={{color: 'white'}}
                                    tooltip="Transkript"
                                    tooltipOptions={{ position: 'top' }}
                                />
                            )}

                            {/* Caption Menu - Fullscreen'de de görünür (altyazı için) */}
                            <CaptionMenu
                                captions={captions}
                                activeCaptionIndex={activeCaptionIndex}
                                onCaptionToggle={handleCaptionToggle}
                            />

                            {/* Fullscreen modunda fullWidth butonu gizli */}
                            {setFullWidth && !fullscreen && (
                                <Button icon="pi pi-arrows-h" size="lg" rounded text onClick={handleFullWidth}/>
                            )}

                            <Button icon={fullscreen ? "pi pi-window-minimize" : "pi pi-window-maximize"} rounded text
                                    size="lg" onClick={handleFullscreen}/>
                            {/*</div>*/}

                            {/*<Button icon="pi pi-fast-backward" rounded text onClick={handleRewind}/>*/}
                            {/*<Button icon="pi pi-fast-forward" rounded text onClick={handleForward}/>*/}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </>

});

export default VideoPlayer;

